import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Juror } from '../../../../redux/contest/types';
import { BrandContestJurorAvatar } from './BrandContestJurorAvatar';
import { FullScreenMoreMenu } from '../../../More/FullScreenMoreMenu';
import { TMoreMenuItem } from '../../../More/more.data';
import { FONT_ENUM } from '../../../../types/global/fonts.types';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_SURFACE_CONTAINER_HIGH,
} from '../../../../pages/BrandDashboard/brandDashboardTokens';
import { midnight } from '../../../../pages/BrandDashboard/contestWizard/midnightStyles';
import { BrandContestJurorProgressRing } from './BrandContestJurorProgressRing';
import { brandJurorCompactCardSx, brandJurorCompactStatBoxSx } from './brandJurorCardLayout';
import { inviteStatusCompactVisual, pulseDot } from './brandJurorCardShared';

const StatBox: React.FC<{ label: string; value: number; highlight?: boolean }> = ({
    label,
    value,
    highlight,
}) => (
    <Box sx={brandJurorCompactStatBoxSx}>
        <Typography
            sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 12,
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: midnight.onSurfaceVariant,
                opacity: 0.6,
                mb: 0.5,
            }}>
            {label}
        </Typography>
        <Typography
            sx={{
                fontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
                fontWeight: 600,
                fontSize: 24,
                lineHeight: 1.2,
                color: highlight ? BRAND_LUXE_PRIMARY : midnight.onSurface,
                fontVariantNumeric: 'tabular-nums',
            }}>
            {String(value).padStart(2, '0')}
        </Typography>
    </Box>
);

const MORE_ICON_BUTTON_SX = {
    p: 0,
    minWidth: 0,
    width: 40,
    height: 40,
    borderRadius: '50%',
    bgcolor: 'transparent',
    border: 'none',
    boxShadow: 'none',
    '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
} as const;

export interface BrandContestJurorCardCompactProps {
    juror: Juror;
    menuItems: TMoreMenuItem[];
    onMenuClick: (type: string, juror: Juror) => void;
}

const BrandContestJurorCardCompact: React.FC<BrandContestJurorCardCompactProps> = ({
    juror,
    menuItems,
    onMenuClick,
}) => {
    const status = inviteStatusCompactVisual(juror.accepted_invite);
    const person = juror.juror;
    const handle = person?.userName ? `@${person.userName}` : '@unknown';

    return (
        <Box sx={brandJurorCompactCardSx}>
            <Box
                sx={{
                    position: 'absolute',
                    top: -96,
                    right: -96,
                    width: 192,
                    height: 192,
                    borderRadius: '50%',
                    bgcolor: BRAND_LUXE_PRIMARY,
                    opacity: 0.05,
                    filter: 'blur(80px)',
                    pointerEvents: 'none',
                }}
            />

            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ position: 'relative' }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0, flex: 1 }}>
                    <BrandContestJurorAvatar
                        user={person}
                        acceptedInvite={Boolean(juror.accepted_invite)}
                        size={56}
                    />
                    <Stack sx={{ minWidth: 0 }}>
                        <Typography
                            sx={{
                                fontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
                                fontWeight: 600,
                                fontSize: 18,
                                lineHeight: 1.3,
                                color: midnight.onSurface,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}>
                            {person?.firstName} {person?.lastName}
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: 'Inter, sans-serif',
                                fontSize: 12,
                                fontWeight: 500,
                                color: midnight.onSurfaceVariant,
                                opacity: 0.7,
                                letterSpacing: '0.02em',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: 180,
                            }}>
                            {handle.length > 14 ? `${handle.slice(0, 14)}...` : handle}
                        </Typography>
                    </Stack>
                </Stack>

                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: `${BRAND_SURFACE_CONTAINER_HIGH}80`,
                        border: `1px solid rgba(72, 72, 71, 0.2)`,
                        transition: 'background-color 0.2s ease',
                        '&:hover': { bgcolor: midnight.surfaceHighest },
                    }}>
                    <FullScreenMoreMenu
                        menu={menuItems}
                        onClick={type => onMenuClick(type, juror)}
                        customIconSx={MORE_ICON_BUTTON_SX}
                        customMoreIcon={
                            <MoreHorizIcon sx={{ color: midnight.onSurfaceVariant, fontSize: 22 }} />
                        }
                    />
                </Box>
            </Stack>

            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                <BrandContestJurorProgressRing percentage={juror.judgment_percentage ?? 0} size={160} />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <StatBox label="Total Assigned" value={juror.total_assigned ?? 0} />
                <StatBox label="Completed" value={juror.judged_submissions ?? 0} highlight />
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    pt: 2,
                    borderTop: `1px solid rgba(72, 72, 71, 0.1)`,
                }}>
                <Box
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1.5,
                        py: 0.75,
                        borderRadius: 999,
                        bgcolor: status.bg,
                        color: status.color,
                        border: `1px solid ${status.border}`,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontWeight: 600,
                        fontSize: 12,
                        whiteSpace: 'nowrap',
                    }}>
                    <Box
                        sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            flexShrink: 0,
                            bgcolor: status.dot,
                            boxShadow: `0 0 8px ${status.dot}`,
                            ...(status.pulse ? { animation: `${pulseDot} 2s ease-in-out infinite` } : {}),
                        }}
                    />
                    {status.label}
                </Box>
            </Box>
        </Box>
    );
};

export { BrandContestJurorCardCompact };
